import React, { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

export const RESET_BUSINESS_CONFIRMATION_PHRASE = "RESET ESPOIR" as const;

export type ResetBusinessDataPayload = {
  adminPassword: string;
  gateSecret: string;
  confirmation: string;
};

type ResetBusinessDataSuccessBody = {
  success: true;
  message: string;
  deletedClients?: number;
  deletedContrats?: number;
  deletedFactures?: number;
  deletedPassagesBySource?: number;
  deletedPassagesByTournee?: number;
  deletedTournees?: number;
  deletedInterventions?: number;
  matchedE2EClientRows?: number;
};

type ResetBusinessDataErrorBody = {
  success: false;
  error: string;
  message: string;
};

type ResetBusinessDataModalProps = {
  open: boolean;
  onClose: () => void;
};

function readBackendMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const d = err.response?.data;
    if (d && typeof d === "object" && "message" in d && typeof (d as { message: unknown }).message === "string") {
      return (d as { message: string }).message;
    }
    if (typeof err.message === "string" && err.message.trim()) return err.message;
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return "La réinitialisation a échoué.";
}

export default function ResetBusinessDataModal({ open, onClose }: ResetBusinessDataModalProps): React.JSX.Element | null {
  const [adminPassword, setAdminPassword] = useState<string>("");
  const [gateSecret, setGateSecret] = useState<string>("");
  const [confirmation, setConfirmation] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const canSubmit = useMemo(() => {
    return (
      adminPassword.trim().length > 0 &&
      gateSecret.trim().length > 0 &&
      confirmation.trim() === RESET_BUSINESS_CONFIRMATION_PHRASE
    );
  }, [adminPassword, gateSecret, confirmation]);

  const blockersText = useMemo(() => {
    const parts: string[] = [];
    if (adminPassword.trim().length === 0) parts.push("mot de passe administrateur");
    if (gateSecret.trim().length === 0) parts.push("code gate");
    if (confirmation.trim() !== RESET_BUSINESS_CONFIRMATION_PHRASE) {
      parts.push(`phrase exacte « ${RESET_BUSINESS_CONFIRMATION_PHRASE} » (sans espace avant/après)`);
    }
    return parts;
  }, [adminPassword, gateSecret, confirmation]);

  const resetForm = useCallback(() => {
    setAdminPassword("");
    setGateSecret("");
    setConfirmation("");
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    onClose();
  }, [submitting, onClose, resetForm]);

  const handleConfirm = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload: ResetBusinessDataPayload = {
        adminPassword,
        gateSecret: gateSecret.trim(),
        confirmation: confirmation.trim(),
      };
      const { data } = await api.post<ResetBusinessDataSuccessBody | ResetBusinessDataErrorBody>(
        "/admin/system/reset-business-data",
        payload
      );
      if (data && typeof data === "object" && "success" in data && data.success === true) {
        const d = data as ResetBusinessDataSuccessBody;
        const clients = d.deletedClients ?? 0;
        const facts = d.deletedFactures ?? 0;
        const ctr = d.deletedContrats ?? 0;
        const pass =
          (d.deletedPassagesBySource ?? 0) + (d.deletedPassagesByTournee ?? 0);
        const matched = d.matchedE2EClientRows ?? 0;
        if (clients === 0 && matched === 0) {
          toast.warning(
            `Aucune fiche client E2E trouvée (références CLI-E2E-* / e-mails seed). Passages nettoyés : ${pass}. Lancez « npm run seed:e2e » si vous attendiez des données de test.`
          );
        } else if (clients === 0 && matched > 0) {
          toast.warning(
            `Aucun client supprimé malgré ${matched} fiche(s) correspondante(s) — vérifier les journaux serveur. Factures supprimées : ${facts}, contrats : ${ctr}, passages : ${pass}.`
          );
        } else {
          toast.success(
            `Réinitialisation : ${clients} client(s), ${ctr} contrat(s), ${facts} facture(s), ${pass} passage(s) (sources / tournée).`
          );
        }
        resetForm();
        onClose();
        window.location.reload();
        return;
      }
      const msg =
        data && typeof data === "object" && "message" in data && typeof data.message === "string"
          ? data.message
          : "Réponse inattendue du serveur.";
      toast.error(msg);
    } catch (e) {
      toast.error(readBackendMessage(e));
    } finally {
      setSubmitting(false);
    }
  }, [adminPassword, gateSecret, confirmation, canSubmit, submitting, onClose, resetForm]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-business-modal-title"
    >
      <div
        className="relative z-[101] w-full max-w-md pointer-events-auto rounded-2xl border border-rose-500/30 bg-slate-900 shadow-2xl ring-1 ring-rose-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => void handleClose()}
          disabled={submitting}
          className="absolute right-3 top-3 rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-40"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-rose-500/20 px-5 pb-4 pt-5">
          <div className="flex items-center gap-2 text-rose-200">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
            <h2 id="reset-business-modal-title" className="text-lg font-semibold tracking-tight">
              Réinitialiser les données métier
            </h2>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-400">
            Cette opération supprime définitivement les enregistrements de test identifiés (clients E2E, passages et
            tournée associés). Elle est protégée par votre mot de passe administrateur et un code gate serveur.
          </p>
        </div>

        <div className="space-y-4 px-5 py-5">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-slate-300">Mot de passe administrateur</span>
            <p className="text-[10px] leading-snug text-slate-500">
              Le mot de passe du <strong className="font-medium text-slate-400">compte utilisateur</strong> avec
              lequel vous êtes connecté (rôle admin), pas un code spécial à cette fenêtre. En local, après{" "}
              <code className="rounded bg-slate-900 px-1 text-[10px]">npm run admin:ensure</code>, c’est souvent{" "}
              <code className="rounded bg-slate-900 px-1 text-[10px]">admin123</code> — sinon utilisez celui défini
              pour votre utilisateur en base.
            </p>
            <input
              type="password"
              autoComplete="current-password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-0 focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 disabled:opacity-50"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-slate-300">Code gate</span>
            <p className="text-[10px] leading-snug text-slate-500">
              Secret défini côté serveur dans <code className="rounded bg-slate-900 px-1 text-[10px]">ADMIN_RESET_GATE_SECRET</code>{" "}
              (<code className="rounded bg-slate-900 px-1 text-[10px]">backend/.env</code>). C’est la valeur exacte
              de cette variable, pas votre mot de passe de session.
            </p>
            <input
              type="password"
              autoComplete="off"
              value={gateSecret}
              onChange={(e) => setGateSecret(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 disabled:opacity-50"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-slate-300">Confirmation exacte</span>
            <p className="text-[10px] leading-snug text-slate-500">
              Ce n’est pas un mot de passe : tapez la <strong className="font-medium text-slate-400">phrase fixe</strong>{" "}
              demandée (copie conforme, majuscules incluses).
            </p>
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={RESET_BUSINESS_CONFIRMATION_PHRASE}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-sm tracking-wide text-slate-100 outline-none placeholder:text-slate-600 focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 disabled:opacity-50"
            />
            <span className="text-[10px] text-slate-500">Saisissez exactement : {RESET_BUSINESS_CONFIRMATION_PHRASE}</span>
          </label>
        </div>

        <div className="space-y-3 border-t border-slate-800 px-5 py-4">
          {!canSubmit && !submitting ? (
            <p className="text-[11px] leading-relaxed text-amber-200/90">
              Le bouton reste grisé tant que tout n’est pas valide. Il manque ou est incorrect :{" "}
              <span className="font-medium text-amber-100/95">{blockersText.join(" ; ")}</span>.
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => void handleClose()}
            disabled={submitting}
            className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!canSubmit || submitting}
            title={
              !canSubmit && !submitting
                ? `Complétez : ${blockersText.join(" ; ")}`
                : undefined
            }
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Réinitialisation en cours…
              </>
            ) : (
              "Confirmer la réinitialisation"
            )}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
